
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import ProfileImageUpload from "./ProfileImageUpload";
import ProfileFormFields from "./ProfileFormFields";

interface ProfileEditFormProps {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profileImageUrl: string | null;
  onSubmit: (formData: FormData) => Promise<void>;
  onUploadImage: (file: File) => Promise<string | null>;
}

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
});

type FormData = z.infer<typeof formSchema>;

const ProfileEditForm = ({
  firstName,
  lastName,
  email,
  profileImageUrl,
  onSubmit,
  onUploadImage,
}: ProfileEditFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || "",
    },
  });

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      // Show success animation
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating your profile.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const url = await onUploadImage(file);
      return url;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your profile image.",
        variant: "destructive",
      });
      return null;
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    const firstInitial = firstName ? firstName.charAt(0) : "";
    const lastInitial = lastName ? lastName.charAt(0) : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <Card className="p-6 relative">
      <div className="absolute top-3 right-3">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="flex items-center bg-green-50 text-green-600 px-3 py-1 rounded-full"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              <span className="text-sm font-medium">Saved</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <h2 className="text-xl font-semibold mb-6">Edit Profile</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
          
          <ProfileImageUpload
            profileImageUrl={profileImageUrl}
            initials={getInitials()}
            onFileSelect={handleImageUpload}
          />
          
          <ProfileFormFields
            firstName={form.watch("firstName")}
            lastName={form.watch("lastName")}
            email={form.watch("email")}
            onChange={(e) => {
              form.setValue(e.target.name as keyof FormData, e.target.value);
            }}
          />
          
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="relative bg-[#607D9B] hover:bg-[#4A607B]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
              
              <AnimatePresence>
                {showSuccess && (
                  <motion.span
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center bg-green-500"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1 text-white" />
                    <span className="text-white">Saved!</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};

export default ProfileEditForm;
